import { HyperliquidBaseClient } from '../core/base-client';
import { PrivyRPCAdapter } from '../adapters/privy-rpc-adapter';

export interface BuilderFeeConfig {
  /** Builder wallet address to receive fees */
  address: string;
  /** Fee in tenths of basis point (e.g., 50 = 5bp = 0.05%) */
  fee: number;
}

export interface HyperliquidBackendConfig {
  privy: {
    appId: string;
    appSecret: string;
    walletId: string; // User's delegated wallet ID
    walletAddress: string; // User's wallet address
  };
  isTestnet?: boolean;
  builder?: BuilderFeeConfig;
}

export interface DelegatedWallet {
  address: string;
  walletId: string;
  chainType: string;
}

/**
 * Hyperliquid client for Node.js backend
 * Uses Privy's delegated wallet RPC API
 */
export class HyperliquidBackendClient extends HyperliquidBaseClient {
  private privyConfig: HyperliquidBackendConfig['privy'];
  private baseUrl = 'https://api.privy.io/v1';

  constructor(config: HyperliquidBackendConfig) {
    const adapter = new PrivyRPCAdapter({
      appId: config.privy.appId,
      appSecret: config.privy.appSecret,
      walletId: config.privy.walletId,
      walletAddress: config.privy.walletAddress,
    });

    super({
      signingAdapter: adapter,
      isTestnet: config.isTestnet,
      builder: config.builder,
    });

    this.privyConfig = config.privy;
  }

  /**
   * Get user's delegated wallets from Privy
   * @param privyUserId The Privy user ID (DID)
   */
  async getUserDelegatedWallets(privyUserId: string): Promise<DelegatedWallet[]> {
    const url = `${this.baseUrl}/users/${privyUserId}`;

    const authHeader = Buffer.from(
      `${this.privyConfig.appId}:${this.privyConfig.appSecret}`
    ).toString('base64');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Basic ${authHeader}`,
        'privy-app-id': this.privyConfig.appId,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user wallets from Privy');
    }

    const user = (await response.json()) as {
      linked_accounts: Array<{
        type: string;
        delegated?: boolean;
        chain_type?: string;
        address: string;
        id: string;
      }>;
    };

    interface PrivyAccount {
      type: string;
      delegated?: boolean;
      chain_type?: string;
      address?: string;
      id?: string;
    }

    return user.linked_accounts
      .filter(
        (account: PrivyAccount) =>
          account.type === 'wallet' &&
          account.delegated === true &&
          account.chain_type === 'ethereum'
      )
      .map((wallet: PrivyAccount) => ({
        address: wallet.address || '',
        walletId: wallet.id || '',
        chainType: wallet.chain_type || '',
      }));
  }

  // All trading methods inherited from HyperliquidBaseClient
}
